import { useState, useEffect } from 'react';

/**
 * Hook to track window width and return if it's above a breakpoint threshold.
 * Uses window resize events instead of ResizeObserver.
 * 
 * @param {number} breakpoint - Width threshold in pixels (default: 700)
 * @returns {boolean} isWideEnough - Whether window width is >= breakpoint
 * 
 * @example
 * const isWideEnough = useWindowWidth(700);
 * return <div>{isWideEnough && <Sidebar />}</div>;
 */
export const useWindowWidth = (breakpoint = 700) => {
  const [isWideEnough, setIsWideEnough] = useState(() => {
    // Initialize with current window width
    return typeof window !== 'undefined' ? window.innerWidth >= breakpoint : true;
  });

  useEffect(() => {
    // Handler to check window width
    const handleResize = () => {
      setIsWideEnough(window.innerWidth >= breakpoint);
    };

    // Set initial value
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, [breakpoint]);

  return isWideEnough;
};
